import get from 'lodash/get';

import { TransferConfig } from '../../config/transfer-config';
import TransferError from '@wetransfer/transfer/helper/error';
import TransferErrorMessages from '@wetransfer/transfer/helper/error/messages';
import { getCurrentOrderAccessToken } from '@wetransfer/transfer/helper/paidTransfers';
import request from '@wetransfer/transfer/modules/http/axios/request';
import { HttpResponseCodes } from '@wetransfer/transfer/modules/http/axios/response';
import retriableRequest from '@wetransfer/transfer/modules/http/axios/retriable-request';
import uploadMonitoringTracker from '@wetransfer/transfer/transfer-bundle/helpers/uploadMonitoringTracker';

function retryApiRequest(method, path, params = null, options = {}) {
  const retries = get(options, 'retries.limit', TransferConfig.api.retry.limit);

  return retriableRequest({
    path: `${TransferConfig.api.endpoint}${path}`,
    method,
    retries,
    // This timeout is used for all requests and not every individual retry.
    // For 500 status code exponential delay is used. What ever comes first,
    // amount of retries or this timeout is gonna stop the API call.
    timeout: TransferConfig.api.timeout,
    params,
    headers: options.headers,
  });
}

export async function verifyToken(transferId, params) {
  try {
    uploadMonitoringTracker().collect(
      '[POST] /transfers/{ID}/verify',
      {
        transfer_id: transferId,
        ...params,
      },
      'INFO'
    );

    const response = await request({
      method: 'POST',
      path: `${TransferConfig.api.endpoint}/transfers/${transferId}/verify`,
      params,
    });

    return response.body;
  } catch (error) {
    uploadMonitoringTracker().collect(
      '[POST] /transfers/{ID}/verify',
      {
        status: error.status,
        statusText: error.statusText,
        stack_trace: error.stack,
        rawError: JSON.stringify(error?.body),
      },
      'ERROR'
    );

    switch (error.status) {
      case HttpResponseCodes.TOO_MANY_REQUESTS:
        throw error;
      default:
        throw new TransferError(TransferErrorMessages.WRONG_VERIFICATION_CODE);
    }
  }
}

export async function resendToken(transferId, params) {
  return await request({
    method: 'POST',
    path: `${TransferConfig.api.endpoint}/transfers/${transferId}/resend-verification-code`,
    params,
  });
}

export async function fetchTransfer(
  transferId,
  secret,
  recipientId = false,
  password = false
) {
  const requestPath = `/transfers/${transferId}/prepare-download`;
  const options = {};

  const params = {
    security_hash: secret,
  };

  if (recipientId) {
    params.recipient_id = recipientId;
  }

  if (password) {
    params.password = password;
  }

  try {
    const response = await retryApiRequest(
      'POST',
      requestPath,
      params,
      options
    );

    return response.body;
  } catch ({ status }) {
    switch (status) {
      case HttpResponseCodes.FORBIDDEN:
        throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
      case HttpResponseCodes.NOT_FOUND:
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function requestPreview(
  transferId,
  secret,
  recipientId = false,
  transferItemId = false,
  password = false,
  geometry
) {
  let initialPath = `/transfers/${transferId}`;

  const params = {
    security_hash: secret,
  };

  // Do we want to fetch a download for single item or entire transfer?
  if (transferItemId !== false) {
    initialPath = `${initialPath}/files/${transferItemId}`;
  }
  initialPath = `${initialPath}/preview`;

  if (geometry && /\d+x\d+/g.test(geometry)) {
    initialPath = `${initialPath}?geometry=${geometry}`;
  }

  if (recipientId) {
    params.recipient_id = recipientId;
  }

  if (password) {
    params.password = password;
  }

  try {
    const response = await retryApiRequest('POST', initialPath, params);

    return response.body;
  } catch (e) {
    throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
  }
}

export async function requestPreviews({
  transferId,
  securityHash,
  height,
  password,
}) {
  const path = `/transfers/${transferId}/previews`;

  try {
    const params = {
      height,
      password,
      security_hash: securityHash,
    };

    const response = await retryApiRequest('POST', path, params);

    return response.body.map((preview) => ({
      fileId: preview.file_id,
      previewUrl: preview.preview_url,
    }));
  } catch (error) {
    throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
  }
}

export async function fetchTransferDownloadLink({
  transferId,
  secret,
  recipientId = false,
  fileIds = false,
  password = false,
  domainUserId = false,
  intent = 'entire_transfer',
}) {
  const path = `/transfers/${transferId}/download`;

  const params = {
    security_hash: secret,
    intent,
  };

  if (recipientId) {
    params.recipient_id = recipientId;
  }

  if (password) {
    params.password = password;
  }

  if (fileIds) {
    params.file_ids = fileIds;
  }

  if (domainUserId) {
    params.domain_user_id = domainUserId;
  }

  const options = {};
  // If an order was created by the buyer, we'll have an order access token
  // mapped to a transfer in local storage which should be sent as a header
  // in the request to authorize the download
  const orderAuthToken = getCurrentOrderAccessToken(transferId);

  if (orderAuthToken) {
    options.headers = { 'x-order-auth': `${orderAuthToken}` };
  }

  try {
    const response = await retryApiRequest('POST', path, params, options);

    return response.body;
  } catch (error) {
    switch (error.status) {
      case HttpResponseCodes.TOO_MANY_REQUESTS:
        throw new TransferError(TransferErrorMessages.TOO_MANY_REQUESTS);
      default:
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
    }
  }
}

export async function exitTransfer(transferId) {
  try {
    uploadMonitoringTracker().collect(
      '[PUT] /transfers/{ID}/browser_exit',
      {
        transfer_id: transferId,
      },
      'INFO'
    );

    const response = await retryApiRequest(
      'PUT',
      `/transfers/${transferId}/browser_exit`
    );

    return response.body;
  } catch (error) {
    uploadMonitoringTracker().collect(
      '[PUT] /transfers/{ID}/browser_exit',
      {
        status: error.status,
        statusText: error.statusText,
        stack_trace: error.stack,
        rawError: JSON.stringify(error?.body),
      },
      'ERROR'
    );

    throw error;
  }
}

export async function cancelTransfer(transferId) {
  try {
    uploadMonitoringTracker().collect(
      '[DELETE] /transfers/{ID}',
      {
        transfer_id: transferId,
      },
      'INFO'
    );

    const response = await retryApiRequest(
      'DELETE',
      `/transfers/${transferId}`
    );

    return response.body;
  } catch (error) {
    uploadMonitoringTracker().collect(
      '[DELETE] /transfers/{ID}',
      {
        status: error.status,
        statusText: error.statusText,
        stack_trace: error.stack,
        rawError: JSON.stringify(error?.body),
      },
      'ERROR'
    );

    throw error;
  }
}

export async function validateRecaptcha(transferId, params) {
  try {
    uploadMonitoringTracker().collect(
      '[POST] /transfers/{ID}/human_verification',
      {
        transfer_id: transferId,
        ...params,
      },
      'INFO'
    );

    const response = await retryApiRequest(
      'POST',
      `/transfers/${transferId}/human_verification`,
      params
    );

    return response.body;
  } catch (error) {
    uploadMonitoringTracker().collect(
      '[POST] /transfers/{ID}/human_verification',
      {
        status: error.status,
        statusText: error.statusText,
        stack_trace: error.stack,
        rawError: JSON.stringify(error?.body),
      },
      'ERROR'
    );

    switch (error.status) {
      case HttpResponseCodes.BAD_REQUEST:
        throw new TransferError(TransferErrorMessages.RECAPTCHA_FAILED);
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function createTransferWithType(type, params) {
  try {
    uploadMonitoringTracker().collect(
      `[POST] /transfers/${type}`,
      {
        params,
      },
      'INFO'
    );

    const response = await retryApiRequest(
      'POST',
      `/transfers/${type}`,
      params
    );

    return response.body;
  } catch (error) {
    uploadMonitoringTracker().collect(
      `[POST] /transfers/${type}`,
      {
        status: error.status,
        statusText: error.statusText,
        stack_trace: error.stack,
        rawError: JSON.stringify(error?.body),
      },
      'ERROR'
    );

    switch (error.status) {
      case HttpResponseCodes.LIMIT_REACHED:
        throw new TransferError(TransferErrorMessages.LIMIT_REACHED);
      case HttpResponseCodes.USER_BLOCKED:
        throw new TransferError(TransferErrorMessages.USER_BLOCKED);
      case HttpResponseCodes.UNAVAILABLE_PARENT:
        throw new TransferError(TransferErrorMessages.UNAVAILABLE_PARENT);
      case HttpResponseCodes.BAD_REQUEST:
        if (get(error, 'response.data.message') === 'too_many_recipients') {
          throw new TransferError(TransferErrorMessages.TOO_MANY_RECIPIENTS);
        }
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      case HttpResponseCodes.EXPECTATION_FAILED:
        throw new TransferError(
          TransferErrorMessages.HUMAN_VERIFICATION_REQUIRED,
          {
            transferId: error.body.id,
          }
        );
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export async function finalizeTransfer(transferId) {
  try {
    uploadMonitoringTracker().collect(
      '[PUT] /transfers/{ID}/finalize',
      {
        transfer_id: transferId,
      },
      'INFO'
    );

    const response = await retryApiRequest(
      'PUT',
      `/transfers/${transferId}/finalize`,
      { wants_storm: true }
    );

    return response.body;
  } catch (error) {
    uploadMonitoringTracker().collect(
      '[PUT] /transfers/{ID}/finalize',
      {
        status: error.status,
        statusText: error.statusText,
        stack_trace: error.stack,
        rawError: JSON.stringify(error?.body),
      },
      'ERROR'
    );

    switch (error.status) {
      case HttpResponseCodes.BAD_REQUEST:
        throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      case HttpResponseCodes.FORBIDDEN:
        throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
      default:
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
    }
  }
}

export const TransferAPI = {
  verifyToken,
  resendToken,
  fetchTransfer,
  requestPreview,
  fetchTransferDownloadLink,
  exitTransfer,
  cancelTransfer,
  validateRecaptcha,
  createTransferWithType,
  requestPreviews,
};
